import { IconSports } from "app/components/Icon/Icon";
import LoadingSpinner from "app/components/LoadingSpinner/LoadingSpinner";
import { BetBuilderEventType } from "app/types/betBuilderType";
import { BetBuilderStates, BetSlipStates } from "app/utils/const";
import React from "react";
import { FormattedMessage } from "react-intl";
import { useDispatch } from "react-redux";
import { BetBuilderHelper } from "../../../../../../../helpers/betBuilderHelper";
import BetBuilderBetSlipButton from "../../BetslipButtons/BetBuilderBetSlipButton";
import BetslipButtons from "../../BetslipButtons/BetslipButtons";
import PotentialProfit from "../../PotentialProfit/PotentialProfit";
import MaxStake from "../../Stake/MaxStake";
import { Stake } from "../../Stake/Stake";
import BetBuilderItems from "./BetBuilderItems/BetBuilderItems";

type Props = {
  betBuilderEvent: BetBuilderEventType;
  stake: number | null;
  setStake: (value: number | null) => void;
  isSubmitDisabled: boolean;
  submitBet: (ev: React.MouseEvent) => void;
  handleMaxClick: (stake: number) => void;
  removeBet: () => void;
  errorMessage: string;
  potentialReturn: number;
};

const BetBuilderContent: React.FC<Props> = ({
  betBuilderEvent,
  stake,
  setStake,
  isSubmitDisabled,
  submitBet,
  handleMaxClick,
  removeBet,
  errorMessage,
  potentialReturn,
}) => {
  const dispatch = useDispatch();
  const showRedundantButton =
    BetBuilderHelper.shouldShowRedundantButton(betBuilderEvent);

  const showLockButton = BetBuilderHelper.shouldShowLockButton(betBuilderEvent);

  // Remove all redundant items using BetBuilderHelper
  const removeAllRedundantItems = () => {
    BetBuilderHelper.removeAllRedundantItems(
      betBuilderEvent,
      dispatch,
      index =>
        betBuilderEvent?.selections[index]?.outcome?.status ===
        BetBuilderStates.UNAVAILABLE
    );
    const payload = { betBuilderEvent };
    // Triggering the validation API after removing redundant items
    dispatch({
      type: "betBuilderModel/validate",
      payload: { eventId: betBuilderEvent.eventId },
    });
  };

  return (
    <div className="collapsible-content bet-builder">
      <div className="bet-builder__title-container">
        <span className="icon-container">
          <IconSports name={betBuilderEvent.sportType} />
        </span>
        <span className="bet-builder__title-text">
          {betBuilderEvent.eventName}
        </span>
      </div>
      <BetBuilderItems
        betBuilderEvent={betBuilderEvent}
        items={betBuilderEvent.betBuilderItems}
      />
      <div className="bet-builder__bottom-section">
        {betBuilderEvent.status === BetSlipStates.FAILED && (
          <div className="bet-builder__error-message">
            {betBuilderEvent.message}
          </div>
        )}

        {errorMessage && (
          <div className="bet-builder__error-message">{errorMessage}</div>
        )}

        <div className="bet-builder__bottom-section__row">
          <Stake stake={stake} setStake={setStake} />
          <PotentialProfit profit={potentialReturn} />
        </div>
        <MaxStake
          maxStake={betBuilderEvent.maxStake}
          handleMaxClick={handleMaxClick}
        />

        {betBuilderEvent.submitting && (
          <LoadingSpinner
            spinner="bar"
            text={
              <FormattedMessage
                tagName="span"
                id="bets-betslip-submission-in-progress"
              />
            }
          />
        )}
        {(showRedundantButton || showLockButton) && (
          <div className="bet-builder__custom__error">
            <div>{betBuilderEvent.errorMessage?.reason}</div>
          </div>
        )}

        {showRedundantButton || showLockButton ? (
          <BetBuilderBetSlipButton
            showRedundantButton={showRedundantButton}
            showLockButton={showLockButton}
            onRemoveAllRedundant={removeAllRedundantItems}
            isError={!!betBuilderEvent.errorMessage?.reason}
            removeBet={removeBet}
          />
        ) : (
          <BetslipButtons
            isSubmitDisabled={isSubmitDisabled}
            submitBet={submitBet}
            showAcceptOddsText={true} // BetBuilder will always show Accept Odds text
            removeBet={removeBet}
          />
        )}
      </div>
    </div>
  );
};

export default BetBuilderContent;
